import React, { ReactElement, useState } from "react";
import { BsTwitter } from "react-icons/bs";
import { FaFacebookSquare, FaLinkedin } from "react-icons/fa";
import { GoLinkExternal } from "react-icons/go";
import { IoPerson } from "react-icons/io5";
import { NoDataOrError } from "../../pages/error/Error";
import { IntegrationDataValueGroup, IntegrationValueLinkIcon, useGetIntegrationUserQuery } from "../../__generated__/api";
import DisplayValue from "../display-value/DisplayValue";
import Loading from "../loading/Loading";
import Styles from "./IntegrationUserData.module.scss";

interface Props {
    userId: string;
}

interface GroupProps {
    group: IntegrationDataValueGroup;
    shouldDisplayGroupName: boolean
}

const ICON_NAME_TO_ICON = {
    [IntegrationValueLinkIcon.Profile]: <IoPerson />,
    [IntegrationValueLinkIcon.External]: <GoLinkExternal />,
    [IntegrationValueLinkIcon.Linkedin]: <FaLinkedin />,
    [IntegrationValueLinkIcon.Twitter]: <BsTwitter />,
    [IntegrationValueLinkIcon.Facebook]: <FaFacebookSquare />,
};

function IntegrationUserDataSection({ group, shouldDisplayGroupName }: GroupProps): ReactElement {
    const [isExpanded, setExpanded] = useState(false);
    let filtered = group.values;
    if (!isExpanded) {
        filtered = filtered.filter(value => value.value && value.isDefaultDisplayed);
    }
    return (
        <div>
            {shouldDisplayGroupName && (
                <div className="mt-2 text-blue-dark mt-3 fw-bolder mb-2 vertical w-100 d-flex justify-content-between">
                    {group.label}
                    {group.links?.isNotEmpty && (
                        <div className="">
                            {group.links?.map(link => (
                                <a
                                    href={link.url as string}
                                    key={link.url as string}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-none ms-2 p-0 align-top text-light"
                                >
                                    <span aria-hidden="true" className="visually-hidden">{link.label}</span>
                                    {ICON_NAME_TO_ICON[link.icon]}
                                </a>
                            ))}
                        </div>
                    )}

                </div>
            )}

            <div className="list-group list-group-flush">
                {filtered.map(value => (
                    <div className="mb-2 list-group-item border-0 p-0 text-gray-800" key={value.label}>
                        <small className="text-gray-500 col-4">
                            {value.label}
                        </small>
                        <div className="col">
                            <DisplayValue type={value.type} value={value.value as string} text={value.text as string} />
                        </div>
                    </div>
                ))}
            </div>
            {(filtered.length < group.values.length || isExpanded) && (
                <button
                    type="button"
                    className="btn btn-link btn-sm d-block"
                    onClick={() => setExpanded(value => !value)}
                >
                    {isExpanded ? "View default" : "View all"}

                </button>
            )}
        </div>
    );
}

export default function IntegrationUserData({ userId }: Props): ReactElement {
    const { data, loading, error } = useGetIntegrationUserQuery({
        variables: {
            id: userId,
        },
    });
    if (loading) {
        return <Loading />;
    }
    if (error || !data) {
        return <NoDataOrError error={error} />;
    }

    const classNames = ["list-group-item text-gray-800"];
    classNames.push(Styles[`user-group`]);

    return (
        <div>

            <div className="h-100 d-flex flex-column">

                <div className="list-group list-group-flush ms-2 flex-grow-1">
                    {data.integrationUserData?.map(integration => (
                        <div className={`mb-3 px-3 mt-3 rounded ${classNames.join(" ")}`} key={integration.id}>
                            <h6 className="">
                                {integration.label}
                            </h6>

                            {integration.groups.map(group => (
                                <IntegrationUserDataSection
                                    key={group.label}
                                    group={group}
                                    shouldDisplayGroupName={integration.groups.length > 1}
                                />
                            ))}

                        </div>
                    ))}

                </div>

            </div>
        </div>
    );
}
